<script setup lang="ts">
import { ref } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface Props {
  lebYm: string;
  paymentDate?: string;
  empJob?: string;  // 직종 필터 ('요양보호사', '사회복지사' 등)
}

const props = defineProps<Props>();

const emit = defineEmits<{
  showSnackbar: [message: string, type: string];
}>();

const store = useStore();
const isLoading = ref(false);

/**
 * 급여명세서 PDF 미리보기 (새 창)
 * hh_emp_sabok_work_info 테이블에 저장된 데이터를 기반으로 PDF 생성
 */
const previewAllWageSlips = async () => {
  const user = store.state.user;
  
  if (!user?.m_sisul_code) {
    emit('showSnackbar', '사용자 정보를 확인할 수 없습니다.', 'error');
    return;
  }

  if (!props.lebYm) {
    emit('showSnackbar', '연월을 선택해주세요.', 'warning');
    return;
  }
  
  isLoading.value = true;
  
  try {
    // PDF 요청 (DB에 저장된 급여 데이터 사용)
    const response = await axios.post('/api/wage-process/wage-slip-pdf', {
      sisulCode: user.m_sisul_code,
      yearMonth: props.lebYm,
      companyName: user.m_sisul_name || '',
      paymentDate: props.paymentDate || '',
      empJob: props.empJob || null  // 직종 필터 (선택사항)
    }, {
      responseType: 'blob'
    });
    
    // Blob URL 생성 후 새 창에서 열기
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // 새 창에서 PDF 열기
    const newWindow = window.open(url, '_blank');
    
    if (!newWindow) {
      emit('showSnackbar', '팝업이 차단되었습니다. 팝업을 허용해주세요.', 'warning');
      // 팝업이 차단된 경우 다운로드로 대체
      const link = document.createElement('a');
      link.href = url;
      link.download = `임금명세서_${props.lebYm}.pdf`;
      link.click();
    } else {
      emit('showSnackbar', '임금명세서를 새 창에서 열었습니다.', 'success');
    }
    
    // URL 정리 (약간의 딜레이 후)
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
  } catch (error: any) {
    console.error('임금명세서 PDF 생성 오류:', error);
    
    // 에러 응답 처리
    if (error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const errorJson = JSON.parse(text);
        emit('showSnackbar', errorJson.message || '임금명세서 생성 중 오류가 발생했습니다.', 'error');
      } catch {
        emit('showSnackbar', '임금명세서 생성 중 오류가 발생했습니다.', 'error');
      }
    } else {
      emit('showSnackbar', error.response?.data?.message || '임금명세서 생성 중 오류가 발생했습니다.', 'error');
    }
  } finally {
    isLoading.value = false;
  }
};

// 외부에서 호출할 수 있도록 expose
defineExpose({
  previewAllWageSlips,
  isLoading
});
</script>

<template>
  <!-- 로딩 오버레이 (필요시 표시) -->
  <v-overlay v-model="isLoading" class="align-center justify-center" >
    <v-progress-circular
      color="primary"
      indeterminate
      size="64"
    />
    <div class="loading-text">임금명세서 생성중...</div>
  </v-overlay>
</template>

<style lang="scss" scoped>
.loading-text {
  margin-top: 16px;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
}
</style>
